// src/components/dashboard/CameraModal.jsx
import React, { useEffect, useState } from "react";
import authAxios from "../../api/authAxios";

export default function CameraModal({
  showModal,
  setShowModal,
  editMode,
  loading,
  form,
  setForm,
  users = [],
  sites = [],
  handleAddOrEditCamera,
}) {
  const [recordingToken, setRecordingToken] = useState(null);
  const v = (x, fallback = "") =>
    x === null || x === undefined ? fallback : x;

  // Filter sites by org
  const scopedSites = (sites || []).filter(
    (s) => Number(s.organization) === Number(form.organization)
  );

  // 🔄 Auto-update defaults when vendor changes
useEffect(() => {
  const vendor = (form.vendor || "axis").toLowerCase();

  // default values
  let defaultPort = 554;
  let defaultPath = "/";

  if (vendor === "axis") {
    defaultPort = form.stream_type === "mjpeg" ? 80 : 554;
    defaultPath =
      form.stream_type === "mjpeg"
        ? "/axis-cgi/mjpg/video.cgi"
        : "/axis-media/media.amp";
  } else if (vendor === "avigilon") {
    defaultPort = 554;
    defaultPath = "/default-av-channel";
  } else if (vendor === "lucid") {
    defaultPort = 554;
    defaultPath = "/stream";
  } else if (vendor === "onvif") {
    defaultPort = 554;
    defaultPath = "/media";
  }

  // 🔒 IMPORTANT:
  // Only apply defaults IF user hasn't manually changed them.
  setForm((prev) => ({
    ...prev,
    port: prev.port ? prev.port : defaultPort,
    path: prev.path ? prev.path : defaultPath,
  }));
}, [form.vendor, form.stream_type]);




  if (!showModal) return null;

  // 🔍 Generate preview URL (masked password)
  const previewUrl = (() => {
    if (!form.ip_address) return "";
    const vendor = (form.vendor || "axis").toLowerCase();
    const scheme = form.stream_type === "mjpeg" ? "http" : "rtsp";
    const auth =
      form.username && form.password
        ? `${form.username}:••••@`
        : form.username
        ? `${form.username}@`
        : "";
    return `${scheme}://${auth}${form.ip_address}:${form.port || 554}${
      form.path || "/"
    }`;
  })();

// ✅ handle recording toggle + token generation
const toggleRecording = async (enable) => {
  if (!form.id) return;
  try {
    const url = enable
      ? `/recordings/manage/${form.id}/enable/`
      : `/recordings/manage/${form.id}/disable/`;
    const res = await authAxios.post(url);
    setForm((prev) => ({ ...prev, recording_enabled: enable }));
    if (res.data.ingest_url) {
      setRecordingToken(res.data);
    } else {
      setRecordingToken(null);
    }
  } catch (err) {
    console.error("Failed to toggle recording:", err);
    alert("Failed to update recording state.");
  }
};



// ─────────────────────────────────────────────
// 1️⃣  Add helper function inside your component
// ─────────────────────────────────────────────
const toggleFullRecording = async (enable) => {
  if (!form.id) return;
  try {
    const url = enable
      ? `/recordings/manage/${form.id}/enable_full/`
      : `/recordings/manage/${form.id}/disable_full/`;
    const res = await authAxios.post(url);
    setForm((prev) => ({ ...prev, full_recording_enabled: enable }));
    if (res.data.ingest_url) {
      // optional, but safe to clear or update token if backend returns one
      setRecordingToken(res.data);
    }
  } catch (err) {
    console.error("Failed to toggle full recording:", err);
    alert("Failed to update full recording state.");
  }
};

function applyCameraDefaults(form) {
  const payload = { ...form };

  const vendor = (payload.vendor || "axis").toLowerCase();
  const streamType = payload.stream_type || "rtsp";

  if (!payload.port) {
    payload.port = streamType === "mjpeg" ? 80 : 554;
  }

  if (!payload.path || payload.path.trim() === "") {
    if (vendor === "axis") {
      payload.path =
        streamType === "mjpeg"
          ? "/axis-cgi/mjpg/video.cgi"
          : "/axis-media/media.amp";
    } else if (vendor === "avigilon") {
      payload.path = "/default-av-channel";
    } else if (vendor === "lucid") {
      payload.path = "/stream";
    } else if (vendor === "onvif") {
      payload.path = "/media";
    } else {
      payload.path = "/";
    }
  }

  return payload;
}


  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-70">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-lg text-white my-8">
          <h3 className="text-xl font-bold mb-4">
            {editMode ? "Edit Camera" : "Add Camera"}
          </h3>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
	      const finalForm = applyCameraDefaults(form);
              await handleAddOrEditCamera(finalForm);
            }}
            className="space-y-3"
          >
            {/* Vendor */}
            <div>
              <label className="block text-sm mb-1 text-gray-300">Vendor</label>
              <select
                value={v(form.vendor, "axis")}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                className="p-2 border border-gray-600 bg-gray-900 rounded w-full"
                required
              >
                <option value="axis">Axis</option>
                <option value="avigilon">Avigilon</option>
                <option value="lucid">Lucid</option>
                <option value="onvif">ONVIF</option>
              </select>
            </div>

            {/* Name / IP / Port */}
            <input
              placeholder="Camera Name"
              value={v(form.name)}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="p-2 border border-gray-600 bg-gray-900 rounded w-full"
              required
            />
            <input
              placeholder="IP Address"
              value={v(form.ip_address)}
              onChange={(e) => setForm({ ...form, ip_address: e.target.value })}
              className="p-2 border border-gray-600 bg-gray-900 rounded w-full"
              required
            />
            <input
              placeholder="Port"
              type="number"
              value={v(form.port, 554)}
              onChange={(e) =>
                setForm({ ...form, port: Number(e.target.value) })
              }
              className="p-2 border border-gray-600 bg-gray-900 rounded w-full"
            />

            {/* Stream Type */}
            <select
              value={v(form.stream_type, "rtsp")}
              onChange={(e) => setForm({ ...form, stream_type: e.target.value })}
              className="p-2 border border-gray-600 bg-gray-900 rounded w-full"
              required
            >
              <option value="rtsp">RTSP</option>
              <option value="mjpeg">MJPEG (HTTP)</option>
            </select>

            {/* Path */}
            <input
              placeholder="Stream Path"
              value={v(form.path)}
              onChange={(e) => setForm({ ...form, path: e.target.value })}
              className="p-2 border border-gray-600 bg-gray-900 rounded w-full"
            />

            {/* Credentials */}
            <input
              placeholder="Username"
              value={v(form.username)}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="p-2 border border-gray-600 bg-gray-900 rounded w-full"
            />
            <input
              type="password"
              placeholder="Password"
              value={v(form.password)}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="p-2 border border-gray-600 bg-gray-900 rounded w-full"
            />

            {/* Location */}
            <input
              placeholder="Location"
              value={v(form.location)}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="p-2 border border-gray-600 bg-gray-900 rounded w-full"
            />

            {/* Site */}
            <div>
              <label className="block text-sm mb-1 text-gray-300">Site</label>
              <select
                value={v(form.site, "")}
                onChange={(e) =>
                  setForm({
                    ...form,
                    site: e.target.value ? Number(e.target.value) : null,
                  })
                }
                className="p-2 border border-gray-600 bg-gray-900 rounded w-full"
                required
              >
                <option value="">Select site…</option>
                {scopedSites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Preview URL */}
            {previewUrl && (
              <div className="text-xs text-gray-400 mt-1">
                <strong>Preview:</strong>{" "}
                <code className="break-all text-green-400">{previewUrl}</code>
              </div>
            )}


{/* Detection & Recording */}
<div className="border-t border-gray-700 pt-3 mt-3">
  <p className="font-semibold text-sm mb-3">Recording & Detection Settings</p>

  {/* Detection Source */}
  <div className="mb-3">
    <label className="block text-sm mb-1 text-gray-300">Detection Method</label>
    <select
      value={form.detection_source || "auto"}
      onChange={(e) => setForm({ ...form, detection_source: e.target.value })}
      className="p-2 border border-gray-600 bg-gray-900 rounded w-full"
    >
      <option value="auto">Auto (Recommended)</option>
      <option value="camera">Camera-native (on-camera motion detection)</option>
      <option value="edge">Edge Device AI (local NVIDIA box)</option>
      <option value="viewmax">Viewmax AI Cloud (server-side)</option>
    </select>
    <p className="text-xs text-gray-400 mt-1">
      <strong>Auto</strong> will prefer the camera’s built-in motion detection
      if available, otherwise it uses Viewmax AI or an Edge Device.
    </p>
  </div>

{/* Recording toggle */}
<div className="flex items-start justify-between bg-gray-900 p-2 rounded mb-3">
  <div className="mr-3">
    <p className="text-sm font-medium">Enable Recording</p>
    <p className="text-xs text-gray-400">
      When enabled, this camera’s motion clips will be saved on the server.
      <br />
      <span className="text-yellow-400 font-semibold">Note:</span> For Axis and
      other smart cameras, this is a two-part setup — it must also be configured
      inside the camera’s own{" "}
      <span className="text-gray-300">Events → Action Rules</span> page with
      the correct recipient URL.
    </p>
  </div>
  <input
    type="checkbox"
    checked={!!form.recording_enabled}
    onChange={(e) => {
      const checked = e.target.checked;
      setForm({ ...form, recording_enabled: checked });
      toggleRecording(checked);
    }}
    className="w-5 h-5 accent-blue-500 flex-shrink-0 mt-1"
  />
</div>



{/* Full Recording toggle */}
<div className="flex items-start justify-between bg-gray-900 p-3 rounded-lg mb-3 shadow-sm">
  <div className="mr-3">
    <p className="text-sm font-medium text-white">Enable Full Recordings (NVR Mode)</p>
    <p className="text-xs text-gray-400">
      When enabled, this camera will continuously record and upload segments to
      the Viewmax Cloud (DigitalOcean Spaces).
    </p>
  </div>
  <input
    type="checkbox"
    checked={!!form.full_recording_enabled}
    onChange={(e) => {
      const checked = e.target.checked;
      setForm({ ...form, full_recording_enabled: checked });
      toggleFullRecording(checked);
    }}
    className="w-5 h-5 accent-blue-500 flex-shrink-0 mt-1"
  />
</div>




{/* Show ingest URL only when recording enabled */}
{form.recording_enabled && recordingToken?.ingest_url && (
  <div className="bg-gray-800 p-2 rounded text-xs mb-3">
    <p className="text-gray-400 mb-1">
      <strong>Step 2 – Configure your Axis camera:</strong>  
      Paste this URL into the camera’s <em>Recipient URL</em> field when creating
      the motion event under <em>Events → Action Rules</em>.
    </p>
    <code className="block break-all text-green-400">
      {recordingToken.ingest_url}
    </code>
    <button
      type="button"
      onClick={() => navigator.clipboard.writeText(recordingToken.ingest_url)}
      className="mt-2 text-blue-400 underline text-xs"
    >
      Copy URL
    </button>
  </div>
)}

  {/* Event Detection toggle — only for non-camera sources */}
  {form.detection_source !== "camera" && (
    <div className="flex items-start justify-between bg-gray-900 p-2 rounded">
      <div className="mr-3">
        <p className="text-sm font-medium">Enable AI Event Detection</p>
        <p className="text-xs text-gray-400">
          Use Viewmax Cloud or your Edge Device to detect motion and objects
          automatically (for cameras without built-in motion detection). Disable this if the camera already performs motion
          detection itself.
        </p>
      </div>
      <input
        type="checkbox"
        checked={!!form.event_detection_enabled}
        onChange={(e) =>
          setForm({ ...form, event_detection_enabled: e.target.checked })
        }
        className="w-5 h-5 accent-green-500 flex-shrink-0 mt-1"
      />
    </div>
  )}
</div>



            {/* Active toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
              />
              <label>Active</label>
            </div>

            {/* Submit / Cancel */}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="bg-gray-600 px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded"
                disabled={loading}
              >
                {loading
                  ? "Saving..."
                  : editMode
                  ? "Update"
                  : "Add"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}


